import React, { useEffect, Suspense, lazy } from 'react';
import {
  BrowserRouter as Router,
  Switch,
  Route,
} from 'react-router-dom';
import { makeStyles } from '@material-ui/styles';
import APIService from '../../service/APIService';
import Login from '../Onboarding/Login/LoginPage';
import Price from '../Onboarding/Price/PricePage';
import Signup from '../Onboarding/Signup/SignupPage';
import ForgotPassword from '../Onboarding/ForgotPassword/ForgotPassword';
import VerifiedRoute from '../Onboarding/VerifiedRoute/VerifiedRoute';
import { Toast } from '../../Material-UI/Components';
import HorizontalLoader from '../../Material-UI/Components/Progress/LinearProgress/LinearProgress';
import { useAppContext, useUser } from '../../store/context';

const AccountDetails = lazy(() => import('../../Components/AccountDetails/AccountDetails'));
const ShopAnalyze = lazy(() => import('../../Components/AccountDetails/ShopAnalyze'));
const PremiumDetails = lazy(() => import('../../Components/AccountDetails/PremiumDetails'));
const Dashboard = lazy(() => import('../Dashboard/Dashboard'));
const Summary = lazy(() => import('../Summary/Summary'));
const ApplicationTrackerPage = lazy(() => import('../ApplicationTracker/ApplicationTrackerPage'));
const ViewJob = lazy(() => import('../ViewJob/ViewJob'));
const SelfAnalysis = lazy(() => import('../SelfAnalysis/SelfAnalysis'));
const LinkedinAnalysis = lazy(() => import('../SelfAnalysis/LinkedinAnalysis'));
const Interview = lazy(() => import('../SelfAnalysis/Interview'));
const AppLoader = lazy(() => import('../AppLoader/AppLoader'));
const TodoLoader = lazy(() => import('../TodoLoader/TodoLoader'));
const ShopLoader = lazy(() => import('../ShopLoader/ShopLoader'));
const PayclientLoader = lazy(() => import('../PayclientLoader/PayclientLoader'));
const LinkedinLoader = lazy(() => import('../LinkedinLoader/LinkedinLoader'));
const LatestLoader = lazy(() => import('../LatestLoader/LatestLoader'));
const ReviewLoader = lazy(() => import('../ReviewLoader/ReviewLoader'));
const AdvLoader = lazy(() => import('../AdvLoader/AdvLoader'));
const SumDash = lazy(() => import('../SumDash/SumDash'));
const ResumeLoader = lazy(() => import('../ResumeLoader/ResumeLoader'));
const AnalyzeLoader = lazy(() => import('../AnalyzeLoader/AnalyzeLoader'));
const OneLoader = lazy(() => import('../OneLoader/OneLoader'));
const JobmatchLoader = lazy(() => import('../JobmatchLoader/JobmatchLoader'));
const SearchLoader = lazy(() => import('../SearchLoader/SearchLoader'));
const AdvsearchLoader = lazy(() => import('../AdvsearchLoader/AdvsearchLoader'));

const useStyles = makeStyles(() => ({
  app: {
    fontSize: '16px',
    color: 'rgb(127, 127, 127)',
    height: '100%',
  },
}));


const App = () => {
  const addUserHook = useUser();

  const classes = useStyles();
  useEffect(() => {
    APIService.isLoggedIn().then(resp => {
      const {
        id, email, name, email_confirmed: confirmed, premium: premium,
      } = resp.data;
      addUserHook(id, email, name, confirmed, premium);
    }).catch((error) => {
      // eslint-disable-next-line no-console
      console.log(error);
    });
  }, []);

  const { state, closeToast } = useAppContext();
  return (
    <div className={classes.app}>
      <Routes />
      <Toast
        show={state.toast.show}
        message={state.toast.message}
        type={state.toast.type}
        onClose={() => closeToast()}
      />
    </div>
  );
};

const VerifiedDashboard = () => (
  <VerifiedRoute>
    <Dashboard />
  </VerifiedRoute>
);

const VerifiedSummary = () => (
  <VerifiedRoute useLegacyShell={false}>
    <Summary />
  </VerifiedRoute>
);

const VerifiedTracker = () => (
  <VerifiedRoute>
    <ApplicationTrackerPage initialView="board" />
  </VerifiedRoute>
);

const VerifiedShopAnalyze = () => (
  <VerifiedRoute>
    <ShopAnalyze />
  </VerifiedRoute>
);
const VerifiedSumDash = () => (
  <VerifiedRoute useLegacyShell={false}>
    <SumDash />
  </VerifiedRoute>
);

const VerifiedShopLoader = () => (
  <VerifiedRoute>
    <ShopLoader />
  </VerifiedRoute>
);
const VerifiedPayclientLoader = () => (
  <VerifiedRoute>
    <PayclientLoader />
  </VerifiedRoute>
);
const VerifiedPrice = () => (
  <VerifiedRoute>
    <Price />
  </VerifiedRoute>
);
const VerifiedAccountDetails = () => (
  <VerifiedRoute>
    <AccountDetails />
  </VerifiedRoute>
);

const VerifiedPremiumDetails = () => (
  <VerifiedRoute>
    <PremiumDetails />
  </VerifiedRoute>
);
const VerifiedInterview = () => (
  <VerifiedRoute>
    <Interview />
  </VerifiedRoute>
);
const VerifiedLinkedinAnalysis = () => (
    <VerifiedRoute>
      <LinkedinAnalysis />
    </VerifiedRoute>
);
const VerifiedSelfAnalysis = () => (
  <VerifiedRoute>
    <SelfAnalysis />
  </VerifiedRoute>
);

const VerifiedTodoLoader = () => (
  <VerifiedRoute>
    <TodoLoader />
  </VerifiedRoute>
);
const VerifiedAppLoader = () => (
  <VerifiedRoute>
    <AppLoader />
  </VerifiedRoute>
);
const VerifiedLatestLoader = () => (
  <VerifiedRoute>
    <LatestLoader />
  </VerifiedRoute>
);
const VerifiedJobmatchLoader = () => (
  <VerifiedRoute>
    <JobmatchLoader />
  </VerifiedRoute>
);
const VerifiedLinkedinLoader = () => (
   <VerifiedRoute>
     <LinkedinLoader />
   </VerifiedRoute>
);
const VerifiedReviewLoader = () => (
  <VerifiedRoute>
    <ReviewLoader />
  </VerifiedRoute>
);
const VerifiedResumeLoader = () => (
  <VerifiedRoute>
    <ResumeLoader />
  </VerifiedRoute>
);
const VerifiedOneLoader = () => (
  <VerifiedRoute>
    <OneLoader />
  </VerifiedRoute>
);
const VerifiedAnalyzeLoader = () => (
  <VerifiedRoute>
    <AnalyzeLoader />
  </VerifiedRoute>
);
const VerifiedAdvLoader = () => (
  <VerifiedRoute>
    <AdvLoader />
  </VerifiedRoute>
);
const VerifiedSearchLoader = () => (
  <VerifiedRoute>
    <SearchLoader />
  </VerifiedRoute>
);
const VerifiedAdvsearchLoader = () => (
  <VerifiedRoute>
    <AdvsearchLoader />
  </VerifiedRoute>
);
const Routes = () => {
  const classes = useStyles();
  const { state } = useAppContext();

  return (
    <Router basename="/track">
      <div className={classes.app}>
        {state.loader ? <HorizontalLoader /> : ''}
        <Suspense fallback={<HorizontalLoader />}>
          <Switch>
            <Route path="/login" component={Login} />
            <Route path="/price" component={Price} />
            <Route path="/signup" component={Signup} />
            <Route path="/forgot" component={ForgotPassword} />
            <Route path="/dashboard" component={VerifiedDashboard} />
            <Route path="/tracker" component={VerifiedTracker} />
            <Route path="/sumdash" component={VerifiedSumDash} />
            <Route path="/summary" component={VerifiedSummary} />
            <Route path="/jobmatch" component={VerifiedJobmatchLoader} />
            <Route path="/self" component={VerifiedSelfAnalysis} />
            <Route path="/linkedin" component={VerifiedLinkedinAnalysis} />
            <Route path="/shopanalyze" component={VerifiedShopAnalyze} />
            <Route path="/interview" component={VerifiedInterview} />
            <Route path="/apploader" component={VerifiedAppLoader} />
            <Route path="/linkedinloader" component={VerifiedLinkedinLoader} />
            <Route path="/todo" component={VerifiedTodoLoader} />
            <Route path="/shop" component={VerifiedShopLoader} />
            <Route path="/payc" component={VerifiedPayclientLoader} />
            <Route path="/latestloader" component={VerifiedLatestLoader} />
            <Route path="/review" component={VerifiedReviewLoader} />
            <Route path="/advloader" component={VerifiedAdvLoader} />
            <Route path="/resumeloader" component={VerifiedResumeLoader} />
            <Route path="/analyzeloader" component={VerifiedAnalyzeLoader} />
            <Route path="/oneloader" component={VerifiedOneLoader} />
            <Route path="/searchloader" component={VerifiedSearchLoader} />
            <Route path="/advsearchloader" component={VerifiedAdvsearchLoader} />
            <Route path="/account" component={VerifiedAccountDetails} />
            <Route path="/premium" component={VerifiedPremiumDetails} />
            <Route path="/application/:applicationId" component={ViewJob} />
            <Route path="/" component={Login} />
          </Switch>
        </Suspense>
      </div>
    </Router>
  );
};


export default App;
